/**
 * Hand-drawn icons, one stroke weight, one visual dialect.
 *
 * An icon font (Material Symbols et al.) is a cross-origin fetch this deployment's COEP
 * posture refuses and every device pays for glyphs it will never see. Each icon here is a
 * few paths on a 24-grid, stroked in `currentColor`, sized by the caller -- the same deal
 * `DoorIcon` struck, collected into one place.
 */
import type { SVGProps } from "react";

const BASE = "flex-none";

export function CloseIcon({ className, ...rest }: SVGProps<SVGSVGElement>) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" className={`${BASE} ${className ?? ""}`} aria-hidden="true" {...rest}>
      <path d="M6 6l12 12M18 6L6 18" />
    </svg>
  );
}

export function MicIcon({ className, ...rest }: SVGProps<SVGSVGElement>) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={`${BASE} ${className ?? ""}`} aria-hidden="true" {...rest}>
      <rect x="9" y="3" width="6" height="11" rx="3" />
      <path d="M5 11a7 7 0 0 0 14 0" />
      <path d="M12 18v3" />
    </svg>
  );
}

export function ArrowRightIcon({ className, ...rest }: SVGProps<SVGSVGElement>) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={`${BASE} ${className ?? ""}`} aria-hidden="true" {...rest}>
      <path d="M4 12h15" />
      <path d="M13 6l6 6-6 6" />
    </svg>
  );
}

/** `ArrowRightIcon` turned round: the Welcome's previous step (#1095). */
export function ArrowLeftIcon({ className, ...rest }: SVGProps<SVGSVGElement>) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={`${BASE} ${className ?? ""}`} aria-hidden="true" {...rest}>
      <path d="M20 12H5" />
      <path d="M11 6l-6 6 6 6" />
    </svg>
  );
}

export function KeyboardIcon({ className, ...rest }: SVGProps<SVGSVGElement>) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" className={`${BASE} ${className ?? ""}`} aria-hidden="true" {...rest}>
      <rect x="3" y="6" width="18" height="12" rx="1.5" />
      <path d="M6.5 10h.01M10.2 10h.01M13.9 10h.01M17.6 10h.01M8 14h8" />
    </svg>
  );
}

export function KeyIcon({ className, ...rest }: SVGProps<SVGSVGElement>) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={`${BASE} ${className ?? ""}`} aria-hidden="true" {...rest}>
      <circle cx="8" cy="15" r="4.2" />
      <path d="M11 12l9-9" />
      <path d="M16.5 6.5L19 9M14 9l2 2" />
    </svg>
  );
}

/** The person icon: you, for the account surface. */
export function PersonIcon({ className, ...rest }: SVGProps<SVGSVGElement>) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={`${BASE} ${className ?? ""}`} aria-hidden="true" {...rest}>
      <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
      <circle cx="12" cy="7" r="4" />
    </svg>
  );
}

/** The wrench: diagnostics and the machinery underneath. */
export function ToolsIcon({ className, ...rest }: SVGProps<SVGSVGElement>) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={`${BASE} ${className ?? ""}`} aria-hidden="true" {...rest}>
      <path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z" />
    </svg>
  );
}

/** The live meter, at rest -- five bars, centre loudest. The mark the boot loader breathes. */
/** The check icon: a take accepted, calibration complete. */
export function CheckIcon({ className, ...rest }: SVGProps<SVGSVGElement>) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={`${BASE} ${className ?? ""}`} aria-hidden="true" {...rest}>
      <path d="M4.5 12.75l6 6 9-13.5" />
    </svg>
  );
}

/**
 * The record-voice-over icon: Google's material symbol of the same name, its published
 * path verbatim -- an icon font cannot cross this app's COEP posture, but its glyphs can.
 */
export function RecordVoiceOverIcon({ className, ...rest }: SVGProps<SVGSVGElement>) {
  return (
    <svg viewBox="0 -960 960 960" fill="currentColor" className={`${BASE} ${className ?? ""}`} aria-hidden="true" {...rest}>
      <path d="m802-343-86-86q46-42 74-100t28-124q0-66-28-124t-74-100l86-86q63 58 101.5 138.5T942-653q0 91-38.5 171.5T802-343ZM674-474l-86-86q20-17 32.5-40.5T633-653q0-27-12.5-51.5T588-746l86-86q38 34 60.5 80t22.5 99q0 53-22.5 99T674-474Zm-317 3q-76 0-129-53t-53-129q0-76 53-129t129-53q76 0 129 53t53 129q0 76-53 129t-129 53ZM18-103v-127q0-37 19.5-69.5T90-349q54-27 120.5-45.5T357-413q80 0 146.5 18.5T624-349q33 17 52.5 49.5T696-230v127H18Z" />
    </svg>
  );
}

/** The lock icon: what stays on the device, for the privacy note. */
export function LockIcon({ className, ...rest }: SVGProps<SVGSVGElement>) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={`${BASE} ${className ?? ""}`} aria-hidden="true" {...rest}>
      <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
      <path d="M7 11V7a5 5 0 0 1 10 0v4" />
    </svg>
  );
}

export function EqBarsIcon({ className, ...rest }: SVGProps<SVGSVGElement>) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" className={`${BASE} ${className ?? ""}`} aria-hidden="true" {...rest}>
      <path d="M4 10v4" />
      <path d="M8 7v10" />
      <path d="M12 4v16" />
      <path d="M16 7v10" />
      <path d="M20 10v4" />
    </svg>
  );
}

/** The chevron: this row goes somewhere. Sits at the trailing edge of an openable row. */
export function ChevronRightIcon({ className, ...rest }: SVGProps<SVGSVGElement>) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={`${BASE} ${className ?? ""}`} aria-hidden="true" {...rest}>
      <path d="m9 18 6-6-6-6" />
    </svg>
  );
}

/** The chevron pointing down: hide what is open, Ask AI's pill while the chat shows (#791). */
export function ChevronDownIcon({ className, ...rest }: SVGProps<SVGSVGElement>) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={`${BASE} ${className ?? ""}`} aria-hidden="true" {...rest}>
      <path d="m6 9 6 6 6-6" />
    </svg>
  );
}

/** The calendar: the day a session happened, in its meta row. */
export function CalendarIcon({ className, ...rest }: SVGProps<SVGSVGElement>) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={`${BASE} ${className ?? ""}`} aria-hidden="true" {...rest}>
      <rect x="3" y="4" width="18" height="18" rx="2" />
      <path d="M16 2v4M8 2v4M3 10h18" />
    </svg>
  );
}

/** The clock: the time a session started. */
export function ClockIcon({ className, ...rest }: SVGProps<SVGSVGElement>) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={`${BASE} ${className ?? ""}`} aria-hidden="true" {...rest}>
      <circle cx="12" cy="12" r="10" />
      <path d="M12 6v6l4 2" />
    </svg>
  );
}

/** The stopwatch: how long a session ran. */
export function TimerIcon({ className, ...rest }: SVGProps<SVGSVGElement>) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={`${BASE} ${className ?? ""}`} aria-hidden="true" {...rest}>
      <path d="M10 2h4" />
      <path d="M12 14l3-3" />
      <circle cx="12" cy="14" r="8" />
    </svg>
  );
}

/** The pair: how many voices a transcript holds. `PersonIcon` grown a companion. */
export function UsersIcon({ className, ...rest }: SVGProps<SVGSVGElement>) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={`${BASE} ${className ?? ""}`} aria-hidden="true" {...rest}>
      <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
      <circle cx="9" cy="7" r="4" />
      <path d="M22 21v-2a4 4 0 0 0-3-3.87" />
      <path d="M16 3.13a4 4 0 0 1 0 7.75" />
    </svg>
  );
}

/**
 * The speaker-notes icon: Google's material symbol of the same name, its published
 * path verbatim -- the same deal `RecordVoiceOverIcon` struck, and for the same
 * reason: an icon font cannot cross this app's COEP posture, but its glyphs can.
 */
export function SpeakerNotesIcon({ className, ...rest }: SVGProps<SVGSVGElement>) {
  return (
    <svg viewBox="0 -960 960 960" fill="currentColor" className={`${BASE} ${className ?? ""}`} aria-hidden="true" {...rest}>
      <path d="M280-400q17 0 28.5-11.5T320-440q0-17-11.5-28.5T280-480q-17 0-28.5 11.5T240-440q0 17 11.5 28.5T280-400Zm0-120q17 0 28.5-11.5T320-560q0-17-11.5-28.5T280-600q-17 0-28.5 11.5T240-560q0 17 11.5 28.5T280-520Zm0-120q17 0 28.5-11.5T320-680q0-17-11.5-28.5T280-720q-17 0-28.5 11.5T240-680q0 17 11.5 28.5T280-640Zm120 240h200v-80H400v80Zm0-120h320v-80H400v80Zm0-120h320v-80H400v80ZM80-80v-720q0-33 23.5-56.5T160-880h640q33 0 56.5 23.5T880-800v480q0 33-23.5 56.5T800-240H240L80-80Zm126-240h594v-480H160v525l46-45Zm-46 0v-480 480Z" />
    </svg>
  );
}

/** The download icon: export transcript. */
export function DownloadIcon({ className, ...rest }: SVGProps<SVGSVGElement>) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={`${BASE} ${className ?? ""}`} aria-hidden="true" {...rest}>
      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
      <polyline points="7 10 12 15 17 10" />
      <line x1="12" y1="15" x2="12" y2="3" />
    </svg>
  );
}

/** A lightning bolt: the result screen's Quick tab, the notes at a glance (#764). */
export function BoltIcon({ className, ...rest }: SVGProps<SVGSVGElement>) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={`${BASE} ${className ?? ""}`} aria-hidden="true" {...rest}>
      <path d="M13 2L4 14h7l-1 8 9-12h-7l1-8z" />
    </svg>
  );
}

/** A sparkle: Ask AI, the result screen's chat about the meeting (#791). */
export function SparkleIcon({ className, ...rest }: SVGProps<SVGSVGElement>) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={`${BASE} ${className ?? ""}`} aria-hidden="true" {...rest}>
      <path d="M11 3l1.9 5.1L18 10l-5.1 1.9L11 17l-1.9-5.1L4 10l5.1-1.9L11 3z" />
      <path d="M19 15v6M16 18h6" />
    </svg>
  );
}

/** A paper plane: Ask AI's send button (#791). */
export function SendIcon({ className, ...rest }: SVGProps<SVGSVGElement>) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={`${BASE} ${className ?? ""}`} aria-hidden="true" {...rest}>
      <path d="M21 3L10 14" />
      <path d="M21 3l-7 18-4-7-7-4 18-7z" />
    </svg>
  );
}

/** A bin: delete, Ask AI's header (#791). */
export function TrashIcon({ className, ...rest }: SVGProps<SVGSVGElement>) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={`${BASE} ${className ?? ""}`} aria-hidden="true" {...rest}>
      <path d="M4 7h16M10 11v6M14 11v6" />
      <path d="M6 7l1 13h10l1-13M9 7V4h6v3" />
    </svg>
  );
}

/** A magnifier: the result screen's transcript search (#766). */
export function SearchIcon({ className, ...rest }: SVGProps<SVGSVGElement>) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={`${BASE} ${className ?? ""}`} aria-hidden="true" {...rest}>
      <circle cx="11" cy="11" r="7" />
      <path d="M20 20l-3.5-3.5" />
    </svg>
  );
}

/** Ticked lines: the result screen's "Actions" checklist (#766). */
export function ChecklistIcon({ className, ...rest }: SVGProps<SVGSVGElement>) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={`${BASE} ${className ?? ""}`} aria-hidden="true" {...rest}>
      <path d="M3 6l2 2 3-3" />
      <path d="M3 16l2 2 3-3" />
      <path d="M12 7h9" />
      <path d="M12 17h9" />
    </svg>
  );
}

/** Two chain links: jump from a decision or action to its moment in the transcript (#766). */
export function LinkIcon({ className, ...rest }: SVGProps<SVGSVGElement>) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={`${BASE} ${className ?? ""}`} aria-hidden="true" {...rest}>
      <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
      <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
    </svg>
  );
}

/** An open book: the result screen's Detailed tab, the notes per person (#764). */
export function BookOpenIcon({ className, ...rest }: SVGProps<SVGSVGElement>) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={`${BASE} ${className ?? ""}`} aria-hidden="true" {...rest}>
      <path d="M2 4h6a4 4 0 0 1 4 4v13a3 3 0 0 0-3-3H2z" />
      <path d="M22 4h-6a4 4 0 0 0-4 4v13a3 3 0 0 1 3-3h7z" />
    </svg>
  );
}

/** The pencil: rename the meeting, beside its heading. */
export function EditIcon({ className, ...rest }: SVGProps<SVGSVGElement>) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={`${BASE} ${className ?? ""}`} aria-hidden="true" {...rest}>
      <path d="M21.17 6.81a1 1 0 0 0-3.98-3.98L3.84 16.17a2 2 0 0 0-.5.83l-1.32 4.35a.5.5 0 0 0 .62.62l4.35-1.32a2 2 0 0 0 .83-.5z" />
      <path d="m15 5 4 4" />
    </svg>
  );
}

/** The check-circle icon: completed items / follow-ups. */
export function CheckCircleIcon({ className, ...rest }: SVGProps<SVGSVGElement>) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={`${BASE} ${className ?? ""}`} aria-hidden="true" {...rest}>
      <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
      <polyline points="22 4 12 14.01 9 11.01" />
    </svg>
  );
}

/** The warning / alert icon. */
export function WarningIcon({ className, ...rest }: SVGProps<SVGSVGElement>) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={`${BASE} ${className ?? ""}`} aria-hidden="true" {...rest}>
      <path d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3Z" />
      <line x1="12" y1="9" x2="12" y2="13" />
      <line x1="12" y1="17" x2="12.01" y2="17" />
    </svg>
  );
}

/**
 * The broadcast beacon: the chirp toggle's mark -- a source with the wave it sends,
 * drawn to both sides like the material `cell_tower` it stands in for.
 */
export function BroadcastIcon({ className, ...rest }: SVGProps<SVGSVGElement>) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={`${BASE} ${className ?? ""}`} aria-hidden="true" {...rest}>
      <circle cx="12" cy="12" r="2" />
      <path d="M16.24 7.76a6 6 0 0 1 0 8.48" />
      <path d="M7.76 16.24a6 6 0 0 1 0-8.48" />
      <path d="M19.07 4.93a10 10 0 0 1 0 14.14" />
      <path d="M4.93 19.07a10 10 0 0 1 0-14.14" />
    </svg>
  );
}

/** The stop-circle: the one control that ends a recording, on every device's dial. */
export function StopCircleIcon({ className, ...rest }: SVGProps<SVGSVGElement>) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={`${BASE} ${className ?? ""}`} aria-hidden="true" {...rest}>
      <circle cx="12" cy="12" r="10" />
      <rect x="9" y="9" width="6" height="6" rx="1" />
    </svg>
  );
}

/** The sync icon: multi-device clock and alignment status. */
export function SyncIcon({ className, ...rest }: SVGProps<SVGSVGElement>) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={`${BASE} ${className ?? ""}`} aria-hidden="true" {...rest}>
      <path d="M21.5 2v6h-6M21.34 15.57a10 10 0 1 1-.57-8.38l5.67-5.67" />
    </svg>
  );
}

export function SettingsIcon({ className, ...rest }: SVGProps<SVGSVGElement>) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" className={`${BASE} ${className ?? ""}`} aria-hidden="true" {...rest}>
      <path d="m10 3-.6 2.2-2 .9-2.1-.6-2 3.5L5 10.6v2.8L3.3 15l2 3.5 2.1-.6 2 .9L10 21h4l.6-2.2 2-.9 2.1.6 2-3.5-1.7-1.6v-2.8L20.7 9l-2-3.5-2.1.6-2-.9L14 3Z" />
      <circle cx="12" cy="12" r="3" />
    </svg>
  );
}
